import { useCallback, useState } from "react";
import { Upload, X, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

const SERVER_URL = import.meta.env.VITE_NESTJS_SERVER;

/**
 * Upload file through API proxy → S3.
 * API handles the S3 upload server-side — works with any S3-compatible storage.
 */
async function uploadFile(
  file: File,
  accessToken: string | null,
): Promise<string> {
  const formData = new FormData();
  formData.append("file", file);

  const res = await fetch(`${SERVER_URL}/api/upload`, {
    method: "POST",
    headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
    body: formData,
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.message ?? "Upload failed");
  }

  const data = await res.json();
  return data.url;
}

/** Single image upload (e.g. logo) */
export function SingleImageUpload({
  value,
  onChange,
}: {
  value: string | null;
  onChange: (url: string | null) => void;
}) {
  const { accessToken } = useAuth();
  const [uploading, setUploading] = useState(false);

  const handleFile = useCallback(
    async (file: File) => {
      setUploading(true);
      try {
        const url = await uploadFile(file, accessToken);
        onChange(url);
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "Błąd uploadu");
      } finally {
        setUploading(false);
      }
    },
    [accessToken, onChange],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      const file = e.dataTransfer.files[0];
      if (file) handleFile(file);
    },
    [handleFile],
  );

  return (
    <div className="space-y-2">
      {value ? (
        <div className="relative inline-block">
          <img
            src={value}
            alt=""
            className="h-32 w-auto rounded-md border object-cover"
          />
          <Button
            type="button"
            variant="destructive"
            size="icon"
            className="absolute -right-2 -top-2 size-6"
            onClick={() => onChange(null)}
          >
            <X className="size-3" />
          </Button>
        </div>
      ) : (
        <label
          className="flex h-32 cursor-pointer items-center justify-center rounded-md border border-dashed transition-colors hover:border-primary hover:bg-primary/5"
          onDragOver={(e) => e.preventDefault()}
          onDrop={handleDrop}
        >
          <input
            type="file"
            className="hidden"
            accept="image/jpeg,image/png,image/webp,image/avif"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleFile(file);
            }}
          />
          {uploading ? (
            <Loader2 className="size-5 animate-spin text-muted-foreground" />
          ) : (
            <div className="flex flex-col items-center gap-1 text-muted-foreground">
              <Upload className="size-5" />
              <span className="text-xs">Przeciągnij lub kliknij</span>
            </div>
          )}
        </label>
      )}
    </div>
  );
}

/** Multi-image gallery upload */
export function GalleryUpload({
  images,
  onAdd,
  onRemove,
}: {
  images: { id: string; url: string }[];
  onAdd: (url: string) => void;
  onRemove: (id: string) => void;
}) {
  const { accessToken } = useAuth();
  const [uploading, setUploading] = useState(false);

  const handleFile = useCallback(
    async (file: File) => {
      setUploading(true);
      try {
        const url = await uploadFile(file, accessToken);
        onAdd(url);
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "Błąd uploadu");
      } finally {
        setUploading(false);
      }
    },
    [accessToken, onAdd],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      const file = e.dataTransfer.files[0];
      if (file) handleFile(file);
    },
    [handleFile],
  );

  return (
    <div className="flex flex-wrap gap-3">
      {images.map((img) => (
        <div key={img.id} className="relative">
          <img
            src={img.url}
            alt=""
            className="h-24 w-24 rounded-md border object-cover"
          />
          <Button
            type="button"
            variant="destructive"
            size="icon"
            className="absolute -right-2 -top-2 size-5"
            onClick={() => onRemove(img.id)}
          >
            <X className="size-3" />
          </Button>
        </div>
      ))}
      <label
        className="flex h-24 w-24 cursor-pointer items-center justify-center rounded-md border border-dashed transition-colors hover:border-primary hover:bg-primary/5"
        onDragOver={(e) => e.preventDefault()}
        onDrop={handleDrop}
      >
        <input
          type="file"
          className="hidden"
          accept="image/jpeg,image/png,image/webp,image/avif"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handleFile(file);
          }}
        />
        {uploading ? (
          <Loader2 className="size-4 animate-spin text-muted-foreground" />
        ) : (
          <Upload className="size-4 text-muted-foreground" />
        )}
      </label>
    </div>
  );
}
